import api from './api';

/**
 * User-Project Assignment Service
 * Handles project assignment operations for RBAC
 */
export const userProjectService = {
  /**
   * Get all user-project assignments (Admin only)
   * @returns {Promise<Array>} List of all assignments
   */
  getAllAssignments: async () => {
    const response = await api.get('/user-projects');
    return response.data.assignments || [];
  },

  /**
   * Get projects assigned to a user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} List of assigned projects
   */
  getUserProjects: async (userId) => {
    const response = await api.get(`/user-projects/user/${userId}`);
    return response.data.projects || [];
  },

  /**
   * Get users assigned to a project (Admin only)
   * @param {string} projectCode - Project code
   * @returns {Promise<Array>} List of assigned users
   */
  getProjectUsers: async (projectCode) => {
    const response = await api.get(`/user-projects/project/${projectCode}`);
    return response.data.users || [];
  },

  /**
   * Assign a project to a user (Admin only)
   * @param {string} userId - User ID
   * @param {string} projectCode - Project code
   * @returns {Promise<Object>} Created assignment
   */
  assignProject: async (userId, projectCode) => {
    const response = await api.post('/user-projects/assign', { userId, projectCode });
    return response.data.assignment;
  },

  /**
   * Assign project to subordinate (SuperUser only)
   * @param {string} userId - Subordinate's user ID
   * @param {string} projectCode - Project code
   * @returns {Promise<Object>} Created assignment
   */
  assignToSubordinate: async (userId, projectCode) => {
    const response = await api.post('/user-projects/assign-subordinate', { userId, projectCode });
    return response.data.assignment;
  },

  /**
   * Unassign project from subordinate (SuperUser only)
   * @param {string} userId - Subordinate's user ID
   * @param {string} projectCode - Project code
   * @returns {Promise<Object>} Deletion confirmation
   */
  unassignFromSubordinate: async (userId, projectCode) => {
    const response = await api.delete('/user-projects/unassign-subordinate', {
      data: { userId, projectCode },
    });
    return response.data;
  },

  /**
   * Remove project assignment from user (Admin only)
   * @param {string} userId - User ID
   * @param {string} projectCode - Project code
   * @returns {Promise<Object>} Deletion confirmation
   */
  unassignProject: async (userId, projectCode) => {
    const response = await api.delete('/user-projects/unassign', {
      data: { userId, projectCode },
    });
    return response.data;
  },

  /**
   * Bulk assign projects to a user (Admin only)
   * @param {string} userId - User ID
   * @param {Array<string>} projectCodes - Array of project codes
   * @returns {Promise<Object>} Bulk assignment result
   */
  bulkAssignProjects: async (userId, projectCodes) => {
    const response = await api.post('/user-projects/bulk-assign', { userId, projectCodes });
    return response.data;
  },

  /**
   * Replace all project assignments for a user (Admin only)
   * @param {string} userId - User ID
   * @param {Array<string>} projectCodes - Array of project codes (empty array removes all)
   * @returns {Promise<Object>} Replacement result
   */
  replaceUserProjects: async (userId, projectCodes) => {
    const response = await api.put('/user-projects/replace', { userId, projectCodes });
    return response.data;
  },

  /**
   * Get my subordinates (SuperUser only)
   * @returns {Promise<Array>} List of subordinates
   */
  getMySubordinates: async () => {
    const response = await api.get('/user-projects/my-subordinates');
    return response.data.subordinates || [];
  },

  /**
   * Get project assignment statistics (Admin only)
   * @returns {Promise<Object>} Assignment statistics
   */
  getStats: async () => {
    const response = await api.get('/user-projects/stats');
    return response.data;
  },
};
